import { FC } from "react";
import { capitalize, Stack, Typography } from "@mui/material";
import { DynamicLayout } from "../../../config/headerPage/headerPage.types";
import { FiltersMenu } from "../../common/FiltersMenu";
import PageHeaderActions from "./PageHeaderActions";
import PageHeaderBreadcrumbs from "./PageHeaderBreadcrumbs";

export type FilterContexType = ("arcs" | "nodes" | "trips" | "entities")[];

type ViewHeaderProps = {
	filterContext?: FilterContexType;
	dynamicLayout?: DynamicLayout;
	hideMenuFilterFromView: boolean;
	title?: string;
	subtitle?: string;
};

/**
 * ViewHeader is the top-level header component rendered inside each View.
 * It is responsible for displaying:
 * - Breadcrumb navigation
 * - Optional dynamic page actions
 * - Optional filter menu
 *
 * This component does not contain business logic itself; it only
 * composes header elements based on the configuration passed by View.
 *
 * @param filterContext            the optional context for the filter menu, used to
 *                                  determine which filters should be visible in the FilterMenu
 *
 * @param dynamicLayout            optional dynamic layout configuration built via
 *                                  buildDynamicLayout; may include pageActions
 *
 * @param hideMenuFilterFromView   if true, hides the FilterMenu even if filterContext is provided;
 *                                  used for specific detail pages where filters should not appear
 *
 * @param title 				   if provided is the Title of the page
 *
 * @param subtitle 				   if provided is the Subtitle of the page, rendered below the title with smaller font size
 *
 * @returns                        the header section containing breadcrumbs,
 *                                  optional page actions, and optional filter menu
 */
export const ViewHeader: FC<ViewHeaderProps> = ({
	filterContext,
	dynamicLayout,
	hideMenuFilterFromView,
	title,
	subtitle,
}) => {
	return (
		<Stack pb={2}>
			<Stack flexDirection="row" justifyContent={"space-between"} alignItems={"center"} height={"60px"}>
				{/* Breadcrumbs */}
				<PageHeaderBreadcrumbs />
				<Stack direction={"row"} spacing={2}>
					{/* Actions */}
					{dynamicLayout?.pageActions && <PageHeaderActions pageActions={dynamicLayout.pageActions} />}

					{filterContext && !hideMenuFilterFromView && <FiltersMenu visibility={filterContext} />}
				</Stack>
			</Stack>
			{title && (
				<Typography fontWeight={400} variant="h4">
					{capitalize(title)}
				</Typography>
			)}
			{subtitle && (
				<Typography mt={1} variant="body2">
					{subtitle}
				</Typography>
			)}
		</Stack>
	);
};
